import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import IconButton from '@material-ui/core/IconButton';
import Icon from 'components/Utils/NewIcon';
import React, { useEffect, useMemo, useState } from 'react';
import DocumentUtils from 'services/DocumentUtils';
import DefaultModal from 'components/Utils/DefaultModal';
import ProgressMessage from 'components/Utils/ProgressMessage';
import './style.scss';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import { useAppSelector } from 'careand-redux/store';
import MenuItem from '@material-ui/core/MenuItem';
import DefaultTextField from 'components/Utils/DefaultTextField';
import LargeResourcePreview from 'components/Utils/LargeResourcePreview';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import AIUtils from 'services/AIUtils';
import ConfirmationButton from '../../../Utils/NewDefaultButton/ConfirmationButton';
import { runSavedPromptTyped } from 'thunks/runSavedPromptTyped';

const TranscribeImmunizationsButton = ({ document }) => {
  const [modal, setModal] = useState(false);
  return (
    document.document_type === DocumentUtils.DOCUMENT_TYPE.immunization_record &&
    document.patient_id && (
      <>
        <IconButton className="ml-2">
          <Icon
            src="syringe"
            height="20px"
            width="20px"
            alt="Transcribe Immunizations"
            showTooltip={true}
            onClick={() => setModal(true)}
          />
        </IconButton>
        {modal && <TranscribeModal document={document} onClose={() => setModal(false)} />}
      </>
    )
  );
};

const TranscribeModal = ({ document, onClose }) => {
  const immunizationDescriptions = useAppSelector((state) => state.immunizationDescriptions);
  const isLoading = useIsLoading(api.AI.runSavedPrompt);
  const dispatch = useAppDispatch();
  const [data, setData] = useState([]);
  const [saving, setSaving] = useState(false);

  const documentId = document?.id;
  const patientId = document?.patient_id;

  useEffect(() => {
    if (documentId) {
      const vaccineList = immunizationDescriptions.map((immunization) => ({
        name: immunization.name,
        description: immunization.description,
        prescription: immunization.prescription,
      }));

      runSavedPromptTyped(
        'Immunization Transcription',
        {
          vaccines: JSON.stringify(vaccineList, null, 2),
        },
        {
          patientId,
          documentId,
        },
      ).then((result) => {
        if (result) {
          setData(AIUtils.parseImmunizationsExtraction(result));
        }
      });
    }
  }, [dispatch, documentId, patientId, immunizationDescriptions]);

  function setImmunization(index, value) {
    setData((prevData) => {
      prevData[index] = value;
      return [...prevData];
    });
  }

  function getFile(dispatchOptions) {
    return dispatch(api.Documents.getDocumentFile({ documentId }), dispatchOptions);
  }

  function addNew(index) {
    setData((prevData) => [
      ...prevData.slice(0, index + 1),
      {
        name: '',
        description: '',
        prescription: '',
        given_date: null,
        expiry_date: null,
        lot: '',
      },
      ...prevData.slice(index + 1),
    ]);
  }

  async function onSave() {
    setSaving(true);
    await Promise.all(
      data
        .filter((d) => d.name)
        .map(async (data, index) => {
          let immunization = {
            patient_id: document.patient_id,
            value: {
              name: data.name,
              description: data.description,
              prescription: data.prescription,
              given_date: data.given_date,
              expiry_date: data.expiry_date,
              lot: data.lot,
              given_by: 'External',
            },
          };
          await dispatch(api.HealthRecords.addImmunization({ immunization }));
        }),
    );
    setSaving(false);
    onClose();
  }

  function onDelete(index) {
    setData((prevData) => prevData.filter((_, i) => i !== index));
  }

  return (
    <DefaultModal
      wrap={true}
      title="Transcribe Immunizations"
      className="transcribe-immunizations-modal large-modal"
      padding={false}
      content={
        <div className="transcribe-wrapper">
          <div className="transcribe-content p-2">
            {isLoading ? (
              <div className="loading-wrapper">
                <ProgressMessage message="Transcribing..." size={50} />
              </div>
            ) : (
              <>
                {data.map((immunization, index) => (
                  <ImmunizationRow
                    key={index + immunization.name + immunization.given_date}
                    index={index}
                    immunization={immunization}
                    onDelete={() => onDelete(index)}
                    setImmunization={(value) => setImmunization(index, value)}
                    onAdd={() => addNew(index)}
                  />
                ))}
                <ConfirmationButton
                  name="Save Immunizations"
                  loading={saving}
                  disabled={data.length === 0}
                  onClick={onSave}
                  className="blue save-button"
                  style={{ marginRight: '20px', width: '250px' }}
                  confirmation={{
                    message:
                      "Are you sure you want to save these immunization to the patient's records?",
                  }}
                />
              </>
            )}
          </div>
          <LargeResourcePreview autoLoad={true} getFile={getFile} />
        </div>
      }
      isOpen={true}
      onRequestClose={onClose}
    />
  );
};

const ImmunizationRow = ({ immunization, setImmunization, onDelete, onAdd, index }) => {
  const immunizationDescriptions = useAppSelector((state) => state.immunizationDescriptions);

  const matchingImmunization = useMemo(
    () => getMatchingImmunization(immunization.name),
    [immunization.name, immunizationDescriptions],
  );

  function getMatchingImmunization(name) {
    return immunizationDescriptions.find((it) => name === it.name);
  }

  function onChangeImmunizationName(name) {
    let match = getMatchingImmunization(name);
    if (match) {
      setImmunization({
        ...immunization,
        name: match.name,
        description: match.description,
        prescription: match.prescription,
      });
    }
  }

  function onChangeImmunizationField(field, value) {
    setImmunization({
      ...immunization,
      [field]: value,
    });
  }

  const disabledInputs = !matchingImmunization || !!matchingImmunization?.description;

  return (
    <div className="immunization-row row">
      <span className="index"># {index + 1}</span>
      <IconButton onClick={onDelete} className="delete">
        <Icon src="trash" width={12} />
      </IconButton>
      <IconButton onClick={onAdd} className="add-new">
        <Icon src="plus" width={12} />
      </IconButton>
      <DefaultSelectField
        name="name"
        label="Immunization"
        value={immunization.name}
        inputs={immunizationDescriptions.map(({ name }) => name)}
        inputElements={immunizationDescriptions.map(({ name, prescription }) => {
          return (
            <MenuItem value={name} key={name}>
              {[prescription, name].filter((str) => str).join(' - ')}
            </MenuItem>
          );
        })}
        onChange={(event) => onChangeImmunizationName(event.target.value)}
      />
      {!disabledInputs && (
        <DefaultTextField
          name="description"
          label="Description"
          value={immunization.description}
          style={{ minWidth: '150px' }}
          onChange={(event) => onChangeImmunizationField('description', event.target.value)}
        />
      )}
      {!disabledInputs && (
        <DefaultTextField
          name="prescription"
          label="Prescription"
          value={immunization.prescription}
          onChange={(event) => onChangeImmunizationField('description', event.target.value)}
        />
      )}
      <DefaultTextField
        name="lot"
        label="Lot #"
        value={immunization.lot}
        onChange={(event) => onChangeImmunizationField('lot', event.target.value)}
      />
      <DefaultDatePicker
        value={immunization.given_date}
        label="Given Date"
        name="given_date"
        onChange={(event, val) => onChangeImmunizationField('given_date', val)}
      />
      <DefaultDatePicker
        value={immunization.expiry_date}
        label="Expiry Date"
        name="expiry_date"
        onChange={(event, val) => onChangeImmunizationField('expiry_date', val)}
      />
      <DefaultTextField
        name="dosage"
        label="Dosage"
        value={immunization.dosage}
        onChange={(event) => onChangeImmunizationField('dosage', event.target.value)}
      />
      <DefaultTextField
        name="site"
        label="Site"
        value={immunization.site}
        onChange={(event) => onChangeImmunizationField('site', event.target.value)}
      />
    </div>
  );
};

export default TranscribeImmunizationsButton;
